// FilterBar — every control that narrows a list, as one group on one surface.
//
// The list pages each composed their filters differently (a six-column grid, a
// flex row, a row that changed with the tab) out of fields that each drew their
// own gray box. This is the one composition: a low surface step holding pill
// fields and pill toggles, wrapping when it must, with a slot at the end for the
// count. The bar is one step up from the page; the fields inside it are the
// filled field's top step (`fieldBase`, shared with every other field in the
// arc), so a search box here and a search box in a drawer cannot disagree about
// tone or focus.
//
// Everything here is a pill on purpose. The style bible's shape scale gives
// interactive elements the full radius; data stays tight. A filter is the most
// interactive thing on a list page.
//
// Nothing in here knows what a SKU or a vendor is. Pages bring labels, options
// and state; this brings shape, keyboard behavior and accessible names.

import React, { useEffect, useState } from 'react';
import { fieldBase } from './review/fieldStyles';
import { TOUCH } from './ui/motion';
import SearchField from './ui/SearchField';
import SegTrack from './ui/SegTrack';
import { Select, type SelectOption } from './ui/Select';

// ── The bar ──────────────────────────────────────────────────────────────────

export const FilterBar: React.FC<{
  children: React.ReactNode;
  /** Names the group for a reader. Defaults to "Filters". */
  label?: string;
  className?: string;
}> = ({ children, label = 'Filters', className = '' }) => (
  <div
    role="group"
    aria-label={label}
    className={`flex flex-wrap items-center gap-x-2 gap-y-2 rounded-lg bg-surface-container-low px-3 py-2 ${className}`}
  >
    {children}
  </div>
);

/** Forces the controls after it onto a new line. For a long chip row that
 *  deserves its own. */
export const FilterBreak: React.FC = () => (
  // -mb-2 cancels one of the two row gaps a zero-height row would otherwise
  // collect, so the break costs the bar's normal gap and not double.
  <div aria-hidden="true" className="h-0 basis-full -mb-2" />
);

/** Parks on the right: counts, live regions, the odd control that is about the
 *  whole list rather than one axis of it. */
export const FilterEnd: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`ml-auto flex items-center gap-3 text-xs text-on-surface-variant tabular-nums ${className}`}>
    {children}
  </div>
);

// ── Fields ───────────────────────────────────────────────────────────────────

const PILL_FIELD = `${fieldBase} rounded-full py-1.5`;

export interface FilterSearchProps {
  value: string;
  onChange: (next: string) => void;
  /**
   * The accessible name. Required, and not the placeholder: a placeholder
   * disappears the moment somebody types, which is exactly when a reader needs
   * the field's name. Say what the search reaches ("Search purchase orders by
   * number or vendor").
   */
  label: string;
  placeholder?: string;
  /** Width. Defaults to a flexible column with a floor and a cap. */
  className?: string;
  autoFocus?: boolean;
  id?: string;
}

/**
 * The list's search: the kit SearchField (`ui/SearchField`) at the bar's flexible width.
 */
export const FilterSearch: React.FC<FilterSearchProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Search…',
  className = 'flex-1 min-w-[12rem] max-w-sm',
  autoFocus,
  id,
}) => (
  <SearchField
    id={id}
    value={value}
    onChange={onChange}
    label={label}
    placeholder={placeholder}
    autoFocus={autoFocus}
    className={className}
  />
);

export interface FilterSelectProps {
  value: string;
  onChange: (next: string) => void;
  /** The accessible name. Visible beside the field unless `hideLabel`. */
  label: string;
  /**
   * Hide the label visually (it stays the field's name). Right when the first
   * option already says what the axis is ("All families", "All locations");
   * wrong when the options are values with no axis in them ("Any", "Cut").
   */
  hideLabel?: boolean;
  /** The `<option>`s. */
  children: React.ReactNode;
  id?: string;
  className?: string;
  disabled?: boolean;
}

let selectSeq = 0;

const COARSE = '(pointer: coarse)';

/** True on a touch screen. Follows a change, such as a tablet docking to a mouse. */
const useCoarsePointer = (): boolean => {
  const [coarse, setCoarse] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(COARSE).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(COARSE);
    if (!mq) return undefined;
    const follow = () => setCoarse(mq.matches);
    follow();
    mq.addEventListener?.('change', follow);
    return () => mq.removeEventListener?.('change', follow);
  }, []);
  return coarse;
};

const textOf = (node: React.ReactNode): string =>
  React.Children.toArray(node)
    .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : React.isValidElement(c) ? textOf((c.props as { children?: React.ReactNode }).children) : ''))
    .join('');

/** The page's `<option>`s as the kit list's options, so one set of children feeds both forms. */
const optionsOf = (children: React.ReactNode): SelectOption[] =>
  React.Children.toArray(children).flatMap((c): SelectOption[] => {
    if (!React.isValidElement(c)) return [];
    const props = c.props as { value?: string | number; disabled?: boolean; children?: React.ReactNode };
    if (c.type === React.Fragment) return optionsOf(props.children);
    if (c.type !== 'option') return [];
    const label = textOf(props.children);
    return [{ key: String(props.value ?? label), label, disabled: !!props.disabled }];
  });

/**
 * A select in a pill. With a mouse it is the native select, which every keyboard already drives;
 * on a touch screen it opens the kit list instead of the phone's own picker, with rows sized for
 * a finger (44px) and the chosen value ticked. Pages pass `<option>`s either way.
 */
export const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  onChange,
  label,
  hideLabel = false,
  children,
  id,
  className = '',
  disabled,
}) => {
  const [autoId] = useState(() => `filter-select-${++selectSeq}`);
  const selectId = id ?? autoId;
  const coarse = useCoarsePointer();
  if (coarse) {
    return (
      <span className={`inline-flex items-center gap-2 whitespace-nowrap text-xs text-on-surface-variant ${className}`}>
        {/* The kit trigger announces "label, value" itself, so the visible label stays out of the name. */}
        {!hideLabel && <span aria-hidden="true">{label}</span>}
        <Select
          id={selectId}
          label={label}
          options={optionsOf(children)}
          value={value}
          onChange={onChange}
          variant="well"
          width="fit"
          size="sm"
          disabled={disabled}
          className={`!rounded-full pl-3 ${TOUCH}`}
        />
      </span>
    );
  }
  return (
    <label
      htmlFor={selectId}
      className={`inline-flex items-center gap-2 whitespace-nowrap text-xs text-on-surface-variant ${className}`}
    >
      {!hideLabel && <span>{label}</span>}
      <span className="relative inline-flex">
        <select
          id={selectId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={hideLabel ? label : undefined}
          disabled={disabled}
          // `w-auto m-0` and `py-1.5` override the legacy element rule in
          // index.css (width 100%, margin auto, 10px vertical padding), which
          // old screens still lay themselves out against.
          className={`${PILL_FIELD} m-0 w-auto cursor-pointer appearance-none pl-3 pr-8 disabled:cursor-default disabled:opacity-50`}
        >
          {children}
        </select>
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 text-on-surface-variant"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    </label>
  );
};

// ── Segmented ────────────────────────────────────────────────────────────────

export interface SegmentedOption {
  key: string;
  label: React.ReactNode;
  /** A quiet figure beside the label: how many rows the segment holds ("Open 8"). Shown at zero. */
  count?: number;
  /** A count on the segment: pending approvals, overdue lines. Hidden at zero. */
  badge?: number;
  /**
   * `alert` paints the badge in the danger color: for a count that is a
   * failure waiting on a human (sends that did not land), never for a routine
   * count. Two gold badges on one page cannot tell those apart.
   */
  badgeTone?: 'alert';
  /** Unavailable right now. aria-disabled rather than disabled, so the
   *  segment stays focusable and its `title` still shows on hover. */
  disabled?: boolean;
  /** Hover text: what the segment shows, or why it is unavailable. Hung on a
   *  box-less span AROUND the button (`display: contents`, so the thumb still
   *  measures the button), because a title on the button itself, or on a span
   *  inside it, replaces the button's accessible name. */
  title?: string;
}

interface SegmentedBase {
  /** Names the group: "Which purchase orders to show". */
  label: string;
  options: SegmentedOption[];
  className?: string;
}
interface SegmentedSingle extends SegmentedBase {
  /** One segment on at a time. The thumb slides between them. */
  multi?: false;
  value: string;
  onChange: (key: string) => void;
}
interface SegmentedMulti extends SegmentedBase {
  /** Any number on at once (line statuses). Each on segment is tinted. */
  multi: true;
  value: ReadonlySet<string> | string[];
  onChange: (key: string) => void;
}
type SegmentedProps = SegmentedSingle | SegmentedMulti;

/**
 * The house segmented control, drawn by the kit's SegTrack: aria-pressed keys in a recessed
 * track, a sliding thumb in single mode, a tint on each on key in multi mode. This keeps the
 * list pages' option shape (`title` is the key's hover text) and lets the keys wrap.
 */
export const Segmented: React.FC<SegmentedProps> = (props) => {
  const { label, options, className = '' } = props;
  const keys = options.map(({ title, ...o }) => ({ ...o, hint: title }));
  return props.multi === true
    ? <SegTrack multi label={label} options={keys} value={props.value} onChange={props.onChange} size="sm" wrap className={className} />
    : <SegTrack label={label} options={keys} value={props.value} onChange={props.onChange} size="sm" wrap className={className} />;
};
